import { BRAND } from '@app/brand'
import { decodePolyline, type LonLat } from '@app/geo'
import { detectLang, getLang, onLangChange, setLang } from '@app/i18n'
import { buildGraph, isParking, localized, parseVenue, type Venue } from '@app/rvf'
import { render } from 'preact'
import { reportScan } from './analytics'
import { App } from './app'
import { loadAssistant, watchEmergencyClass } from './assistant'
import { carParam, loadCar } from './car'
import { DEFAULT_VENUE_ID, venueDataUrl } from './config'
import { scheduleIdle } from './idle'
import { importChunk } from './lazy-retry'
import { parseVenuePath } from './link/venue-link'
import { warmEngine, warmVenueMapModule } from './map/engine-warm'
import { loadAlertProfile } from './nav/alerts'
import { loadOccupants } from './occupants'
import { loadShoppingList } from './shopping'
import {
  alertProfile,
  anchorId,
  appState,
  emergencyDrill,
  emergencyFrame,
  emergencyHazard,
  geoFix,
  geoStart,
  graph,
  isOffline,
  langTick,
  launchLink,
  parkedCar,
  pendingTargetId,
  signRoute,
  swReady,
  updateReady,
  venue,
  venueMap,
  venueVerified,
} from './state'
import { warmAssetCacheAfterInstall } from './sw/warm-assets'
import { warmEmergencyPanel } from './ui/emergency-lazy'
import { warmFollowHud } from './ui/follow-lazy'
import './terra.css'
import './aurora.css'
import './components.css'
import './widgets.css'
import './emergency.css'
import './emergency-widgets.css'
import './emergency-where.css'
import './emergency-svg.css'
import './styles.css'
import './header.css'
import './target-card.css'
import './orient.css'
import './route.css'
import './hud.css'
import './ar.css'
import './emergency-map-bar.css'
import './search.css'
import { loadCategories } from './categories'
import './directory.css'
import './sos-bar.css'
import './volume-switch.css'
import './viewer-extras.css'
import './promo.css'
import './terra-panels.css'
import './mall.css'
import './cards.css'
import './card-access.css'
import './shopping.css'
import './packs.css'
import './edge-dock.css'
import './qr-scan.css'
import './sign-route.css'
import './staff-hazard.css'

/** Восстановление места машины: сохранённое в браузере или из `?car=` (durability). */
function restoreCar(parsed: Venue): void {
  const stored = loadCar(parsed.venue.id)
  if (stored && parsed.units.some((u) => u.id === stored.targetId)) {
    parkedCar.value = stored
    return
  }
  const fromUrl = carParam(location.search)
  const unit = fromUrl
    ? parsed.units.find((u) => u.id === fromUrl && isParking(parsed, u.category))
    : null
  if (unit) {
    parkedCar.value = {
      venueId: parsed.venue.id,
      targetId: unit.id,
      levelId: unit.level,
      label: localized(unit.name, getLang(), parsed.venue.default_language) || unit.id,
      savedAt: '',
    }
  }
}

const LANG_STORE_KEY = 'lang'

function updateTitle(): void {
  const v = venue.value
  document.title = v
    ? `${localized(v.venue.name, getLang(), v.venue.default_language)} — ${BRAND.productName}`
    : BRAND.productName
}

setLang(detectLang(localStorage.getItem(LANG_STORE_KEY), navigator.languages ?? []))
// Справочник категорий (U7) — в фоне: карта не ждёт его, подписи обновятся сами.
loadCategories()
document.documentElement.lang = getLang()
onLangChange(() => {
  localStorage.setItem(LANG_STORE_KEY, getLang())
  document.documentElement.lang = getLang()
  langTick.value++
  venueMap.value?.refreshLabels()
  updateTitle()
})
updateTitle()

/** Гео-старт для карточки цели — только при УЖЕ выданном разрешении (без промпта). */
async function cacheGeoStartIfGranted(): Promise<void> {
  if (!('permissions' in navigator) || !navigator.geolocation) return
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    if (status.state !== 'granted') return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const point: LonLat = [pos.coords.longitude, pos.coords.latitude]
        geoStart.value = point
        // Фикс с точностью и временем — для «где человек» при тревоге.
        geoFix.value = { point, accuracyM: pos.coords.accuracy, at: pos.timestamp || Date.now() }
      },
      () => undefined,
      { maximumAge: 60_000, timeout: 5_000 },
    )
  } catch {
    // permissions API недоступен — старт останется от якоря/входа
  }
}
void cacheGeoStartIfGranted()

// H1: живой индикатор сети — бейдж переключается на «карта из кеша».
window.addEventListener('offline', () => {
  isOffline.value = true
})
window.addEventListener('online', () => {
  isOffline.value = false
})

// Оффлайн-бейдж честен только после реальной готовности service worker.
if ('serviceWorker' in navigator) {
  void navigator.serviceWorker.ready.then((reg) => {
    swReady.value = true
    // Живущая вкладка/PWA не перезагружает страницу — сама проверяет новый
    // билд: раз в минуту, при возврате вкладки из фона и из BFCache
    // (иначе свежий деплой виден только после жёсткого рефреша).
    const check = (): void => {
      void reg.update().catch(() => {
        // оффлайн/сеть моргнула — проверим в следующий раз
      })
    }
    setInterval(check, 60_000)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check()
    })
    window.addEventListener('pageshow', (e) => {
      if ((e as PageTransitionEvent).persisted) check()
    })
  })
  // Новый деплой активировал новый SW под живой страницей (autoUpdate +
  // skipWaiting): без reload вкладка остаётся на старом билде до следующего
  // визита. В спокойных состояниях перезагружаемся тихо; посреди ведения /
  // выбора цели не рвём поток — показываем вежливый промт «Обновить»
  // (updateReady → тост в App), пользователь решает сам.
  let hadController = navigator.serviceWorker.controller !== null
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      // Первая установка SW на чистом визите — контроллер появился, это не апдейт.
      // Чанки, пришедшие до захвата страницы, докладываем в кеш (sw/warm-assets.ts).
      hadController = true
      warmAssetCacheAfterInstall()
      return
    }
    const calm =
      appState.value === 'overview' || appState.value === 'loading' || appState.value === 'error'
    if (calm) {
      location.reload()
    } else {
      updateReady.value = true
    }
  })
}

alertProfile.value = loadAlertProfile()

// Корень без площадки — глобальный каталог listed-площадок (запрос 24.07);
// контракт /v/{venue}?a=&to= неизменен (ADR 0004).
// Исходный адрес и момент открытия — до любых правок URL: свежесть QR (`?a=`)
// решает, «на месте» ли человек при тревоге (emergency/presence.ts isQrFresh).
launchLink.value = { search: location.search, openedAtMs: Date.now() }
const parsedLink = parseVenuePath(location.pathname, location.search)
const isDirectory = parsedLink === null && location.pathname === '/'
// Раскрытие ранжирования (UCPD 7(4a)): отдельный адрес, чтобы ссылка из
// рекламного блока работала и когда карта ещё не загрузилась.
const isPromoRanking = location.pathname.replace(/\/$/, '') === '/promo-ranking'
const link = parsedLink ?? { venueId: DEFAULT_VENUE_ID }
anchorId.value = link.anchor ?? null
pendingTargetId.value = link.to ?? null
// Открытие с QR-якорем = скан щита — единственная метрика полевого теста.
if (link.anchor) reportScan(link.venueId, link.anchor)

const root = document.getElementById('app')
if (root) {
  root.replaceChildren()
  if (isPromoRanking) {
    void importChunk(() => import('./ui/promo-ranking')).then(({ PromoRankingPage }) => {
      render(<PromoRankingPage />, root)
    })
  } else if (isDirectory) {
    void importChunk(() => import('./ui/directory')).then(({ DirectoryPage }) => {
      render(<DirectoryPage />, root)
    })
  } else {
    render(<App />, root)
  }
}

async function loadVenue(venueId: string): Promise<void> {
  try {
    const response = await fetch(venueDataUrl(venueId))
    if (!response.ok) throw new Error(`venue fetch: HTTP ${response.status}`)
    const parsed = parseVenue(await response.json())
    venue.value = parsed
    graph.value = buildGraph(parsed.network)
    restoreCar(parsed)
    // Список покупок: ?list= из расшаренной ссылки сильнее сохранённого.
    loadShoppingList(parsed, new URLSearchParams(location.search).get('list'))
    appState.value = 'overview'
    updateTitle()
    // Чанки ЧС и ведения — в простое после карты (не критический путь, но и
    // не «когда понадобится»): посетитель без сети должен получить рабочее
    // «Укрытие»/«Поехали» с первого визита, а не со второго (план 24.09 п.1).
    // Таймаут 4с держит прогрев впереди второго прохода warm-assets (8с).
    scheduleIdle(() => {
      void warmEmergencyPanel()
      void warmFollowHud()
    }, 4000)
    // Сеть пропала раньше прогрева (24.09) — догреваем, как только вернётся:
    // service worker положит чанки в кеш, и следующий оффлайн их уже увидит.
    // Прогретое повторно не качается (модульный кеш в *-lazy.tsx).
    window.addEventListener('online', () => {
      void warmEmergencyPanel()
      void warmFollowHud()
    })
    // Живой слой карточек — после карты, не блокируя её (ADR 0014).
    void loadOccupants(parsed.venue.id)
    // AI-ассистент (ADR 0020): конфиг после карты, скрипт — в idle-паузу;
    // класс ЧС ведём в любом случае (скрывает виджет в аварийном режиме).
    watchEmergencyClass()
    void loadAssistant(parsed.venue.id)
    // Живая тревога владельца: лёгкий опрос — карта обязана узнать о пожаре,
    // даже если открыта давно (emergency/live.ts; код-сплит).
    void importChunk(() => import('./emergency/live')).then(({ startAlertWatch }) =>
      startAlertWatch(parsed),
    )
    // Опасность перекрыла построенный маршрут — перестроить сам (route-guard.ts).
    void importChunk(() => import('./emergency/route-guard')).then((m) => m.watchHazardRoutes())
    // Режим персонала (City Resilience Hack): тихая проверка прав кабинета —
    // гостю кнопка «Отметить опасность» не покажется вовсе (staff/staff-api.ts).
    void importChunk(() => import('./staff/staff-api')).then((m) =>
      m.checkStaffAccess(parsed.venue.id),
    )
    // Бейдж «Организация проверена» (N2) — лениво, вне критического пути.
    void fetch(`/api/venue-badge/${parsed.venue.id}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((badge: { verified?: boolean } | null) => {
        venueVerified.value = badge?.verified === true
      })
      .catch(() => undefined)
    // Аварийный режим: ?e= присутствует И площадка имеет ключи → код-сплит (G4).
    void maybeActivateEmergency(parsed, location.search)
    // Демо-режим ЧС (?drill=fire|flood|evac|uav) — всегда с водяным знаком учений.
    void maybeActivateDrill(parsed, location.search)
  } catch (error) {
    console.error('venue load failed', error)
    appState.value = 'error'
  }
}

/**
 * Lazy-загрузка и приём аварийного кадра `?e=` (G4, ADR 0009): подписанный —
 * верификация, переданный соседом без сети — как «не проверено» (relay.ts).
 * Код-сплит: emergency-чанк не попадает в критический путь загрузки.
 * Невалидный/отсутствующий параметр → молча игнорируем (анти-паника).
 */
async function maybeActivateEmergency(parsed: Venue, search: string): Promise<void> {
  const eParam = new URLSearchParams(search).get('e')
  if (!eParam) return
  try {
    const { acceptEmergencyParam } = await importChunk(() => import('./emergency/relay'))
    const frame = await acceptEmergencyParam(eParam, parsed)
    if (frame) emergencyFrame.value = frame
  } catch {
    // Молча игнорируем: ошибка верификации = не активируем режим
  }
}

async function maybeActivateDrill(parsed: Venue, search: string): Promise<void> {
  const { parseDrillParam, buildDrillFrame } = await importChunk(() => import('./emergency/drill'))
  const hazard = parseDrillParam(search)
  if (!hazard) return
  emergencyHazard.value = hazard
  emergencyDrill.value = true
  emergencyFrame.value = buildDrillFrame(parsed.venue.id, hazard)
}

/** Тихое автообновление пакетов — после старта, чтобы не спорить с картой за сеть. */
const PACK_UPDATE_DELAY_MS = 10_000

if (!isDirectory && !isPromoRanking) {
  // План 21.09 п.1: движок и модуль карты греются параллельно с venue.json —
  // раньше import('maplibre-gl') стартовал только после ответа с картой.
  void warmEngine()
  warmVenueMapModule().catch(() => undefined)
  // Путь с таблички (&r=, уровень 0): две точки и больше — рисуем даже без venue.json.
  const signPoints = link.r ? decodePolyline(link.r) : []
  signRoute.value = signPoints.length >= 2 ? signPoints : null
  void loadVenue(link.venueId)
  // Код пакетов — ленивый чанк: стартовому бандлу он не нужен (check:bundle).
  setTimeout(
    () => void import('./packs/update').then((m) => m.checkPackUpdates()).catch(() => undefined),
    PACK_UPDATE_DELAY_MS,
  )
}
